/**
 * Token manager for automatic JWT injection.
 *
 * This module provides a way to set a token getter function during app
 * initialization. The httpClient uses this to automatically inject the
 * Authorization header without requiring React dependencies.
 *
 * @example
 * // In App.tsx or index.tsx (after Redux store is created)
 * import { setTokenGetter } from '@/infrastructure/http/tokenManager';
 * import { store } from '@/redux/store';
 *
 * setTokenGetter(() => store.getState().userLogin.userInfo?.accessToken);
 */

type TokenGetter = () => string | undefined;

let tokenGetter: TokenGetter | null = null;

/**
 * Set the function used to retrieve the current auth token.
 * Call this once during app initialization.
 */
export const setTokenGetter = (getter: TokenGetter): void => {
  tokenGetter = getter;
};

/**
 * Get the current auth token.
 * Returns undefined if no token getter is set or if the user is not authenticated.
 */
export const getToken = (): string | undefined => {
  return tokenGetter?.();
};

/**
 * Check if the token getter has been configured.
 */
export const isTokenGetterConfigured = (): boolean => {
  return tokenGetter !== null;
};
